import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCourses, useTasks } from "../hooks/useStudyOS";
import { deleteCourse, getTodayPlan, savePlan, subscribeToPlan } from "../lib/db";

export default function AcademicScreen() {
  const navigate = useNavigate();
  const { courses } = useCourses();
  const { pending } = useTasks();
  const [plan, setPlan] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [planForm, setPlanForm] = useState({});
  const [courseForm, setCourseForm] = useState({});
  const [selectedColor, setSelectedColor] = useState("#7C6EF8");

  const COURSE_COLORS = ["#7C6EF8","#2ECC8A","#F0A429","#F05D5D","#3BB8F0","#E87BDB","#F07C3B"];

  useEffect(() => {
    getTodayPlan().then(setPlan);
    const unsub = subscribeToPlan(setPlan);
    return unsub;
  }, []);

  const today = new Date();
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const calDays = [];
  for (let i = -2; i <= 6; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    calDays.push({ d, isToday: i === 0 });
  }

  const togglePlan = async (i) => {
    const updated = plan.map((item, idx) =>
      idx === i ? { ...item, done: !item.done } : item
    );
    setPlan(updated);
    await savePlan(updated);
  };

  const deletePlan = async (i) => {
    const updated = plan.filter((_, idx) => idx !== i);
    setPlan(updated);
    await savePlan(updated);
  };

  const addPlanSession = async () => {
    if (!planForm.subject) return;
    const newSession = { ...planForm, done: false, createdAt: new Date().toISOString() };
    const updated = [...plan, newSession];
    setPlan(updated);
    await savePlan(updated);
    setPlanForm({});
    setShowPlanModal(false);
  };

  const startSession = (session) => {
    navigate("/focus", { state: { subject: session.subject } });
  };

  const handleAddCourse = async () => {
    if (!courseForm.name) return;
    const { addCourse } = await import("../lib/db");
    await addCourse({
      name: courseForm.name,
      code: courseForm.code || "",
      credits: courseForm.credits || "",
      color: selectedColor,
    });
    setCourseForm({});
    setShowCourseModal(false);
  };

  return (
    <div className="screen-wrap">

      {/* Calendar strip */}
      <div style={styles.sLabel}>This week</div>
      <div style={styles.calStrip}>
        {calDays.map(({ d, isToday }) => (
          <div key={d.toISOString()} style={{...styles.calDay, ...(isToday ? styles.calToday : {})}}>
            <span style={{...styles.cdName, ...(isToday?{color:"#fff"}:{})}}>{days[d.getDay()]}</span>
            <span style={{...styles.cdNum, ...(isToday?{color:"#fff"}:{})}}>{d.getDate()}</span>
            <span style={{...styles.cdDot, background: isToday?"rgba(255,255,255,.5)":"transparent"}}/>
          </div>
        ))}
      </div>

      {/* Courses */}
      <div style={styles.rowHeader}>
        <div style={styles.sLabel}>My courses</div>
        <button style={styles.addBtn} onClick={() => setShowCourseModal(true)}>+ Add course</button>
      </div>

      {courses.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📚</div>
          <div style={styles.emptyTitle}>No courses yet</div>
          <div style={styles.emptyDesc}>Add your courses to organise tasks and notes.</div>
        </div>
      ) : (
        courses.map(c => (
          <div key={c.id} style={styles.courseCard}>
            <div style={{...styles.courseDot, background: c.color}}/>
            <div style={styles.courseInfo}>
              <div style={styles.courseName}>{c.name}</div>
              <div style={styles.courseCode}>{c.code} {c.credits ? `· ${c.credits} credits` : ""}</div>
            </div>
            <span style={styles.taskBadge}>{pending.filter(t => t.courseId === c.id).length} tasks</span>
            <button style={styles.delBtn} onClick={() => deleteCourse(c.id)}>×</button>
          </div>
        ))
      )}

      {/* Study Plan */}
      <div style={styles.rowHeader}>
        <div style={styles.sLabel}>Today's study plan</div>
        <button style={styles.addBtn} onClick={() => setShowPlanModal(true)}>+ Add session</button>
      </div>

      {plan.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📅</div>
          <div style={styles.emptyTitle}>No sessions planned</div>
          <div style={styles.emptyDesc}>Add study sessions to plan your day. Incomplete sessions carry over to tomorrow.</div>
        </div>
      ) : (
        plan.map((p, i) => (
          <div key={i} style={{...styles.planCard, opacity: p.done ? 0.5 : 1}}>
            <div
              style={{...styles.check, ...(p.done ? styles.checkDone : {})}}
              onClick={() => togglePlan(i)}>
              {p.done && <span style={{fontSize:"10px",color:"#fff"}}>✓</span>}
            </div>
            <div style={styles.planBody}>
              <div style={{...styles.planSubj, textDecoration: p.done?"line-through":"none"}}>{p.subject}</div>
              <div style={styles.planTask}>{p.task}</div>
              {p.time && <div style={styles.planTime}>{p.time}</div>}
            </div>
            {!p.done && (
              <button style={styles.startBtn} onClick={() => startSession(p)}>▶ Start</button>
            )}
            <button style={styles.delBtn} onClick={() => deletePlan(i)}>×</button>
          </div>
        ))
      )}

      {/* Plan Modal */}
      {showPlanModal && (
        <div style={styles.overlay} onClick={e => e.target===e.currentTarget && setShowPlanModal(false)}>
          <div style={styles.modalBox}>
            <div style={styles.handle}/>
            <div style={styles.modalTitle}>Add study session</div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Subject *</label>
              <input style={styles.input} placeholder="e.g. Statistics revision"
                value={planForm.subject||""} onChange={e=>setPlanForm({...planForm,subject:e.target.value})}/>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>What to do</label>
              <input style={styles.input} placeholder="e.g. Practice problem sets"
                value={planForm.task||""} onChange={e=>setPlanForm({...planForm,task:e.target.value})}/>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Time</label>
                <input style={styles.input} placeholder="e.g. 2:00 – 3:30pm"
                  value={planForm.time||""} onChange={e=>setPlanForm({...planForm,time:e.target.value})}/>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Course</label>
                <select style={styles.input} value={planForm.courseId||""}
                  onChange={e=>setPlanForm({...planForm,courseId:e.target.value})}>
                  <option value="">General</option>
                  {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <button style={styles.submitBtn} onClick={addPlanSession}>Add session</button>
          </div>
        </div>
      )}

      {/* Course Modal */}
      {showCourseModal && (
        <div style={styles.overlay} onClick={e => e.target===e.currentTarget && setShowCourseModal(false)}>
          <div style={styles.modalBox}>
            <div style={styles.handle}/>
            <div style={styles.modalTitle}>Add course</div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Course name *</label>
              <input style={styles.input} placeholder="e.g. Macroeconomics"
                value={courseForm.name||""} onChange={e=>setCourseForm({...courseForm,name:e.target.value})}/>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Course code</label>
                <input style={styles.input} placeholder="e.g. ECON 301"
                  value={courseForm.code||""} onChange={e=>setCourseForm({...courseForm,code:e.target.value})}/>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Credits</label>
                <input style={styles.input} type="number" placeholder="3"
                  value={courseForm.credits||""} onChange={e=>setCourseForm({...courseForm,credits:e.target.value})}/>
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Colour</label>
              <div style={{display:"flex",gap:"8px",flexWrap:"wrap"}}>
                {COURSE_COLORS.map(c => (
                  <div key={c} onClick={()=>setSelectedColor(c)}
                    style={{width:"28px",height:"28px",borderRadius:"50%",background:c,
                    border:selectedColor===c?"3px solid #fff":"3px solid transparent",cursor:"pointer"}}/>
                ))}
              </div>
            </div>
            <button style={styles.submitBtn} onClick={handleAddCourse}>Add course</button>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  sLabel:      { fontSize:"11px", fontWeight:600, color:"#5C5B63", textTransform:"uppercase", letterSpacing:".08em", margin:"0 0 10px" },
  calStrip:    { display:"flex", gap:"6px", overflowX:"auto", paddingBottom:"4px", marginBottom:"20px" },
  calDay:      { flexShrink:0, width:"46px", padding:"10px 0", borderRadius:"10px", border:"1px solid transparent", display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" },
  calToday:    { background:"#7C6EF8" },
  cdName:      { fontSize:"9px", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em", color:"#5C5B63" },
  cdNum:       { fontSize:"17px", fontWeight:700, color:"#F0EFE8" },
  cdDot:       { width:"4px", height:"4px", borderRadius:"50%" },
  rowHeader:   { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"10px", marginTop:"22px" },
  addBtn:      { fontSize:"12px", color:"#7C6EF8", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", fontWeight:600 },
  courseCard:  { background:"#18181F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:"16px", padding:"14px 16px", marginBottom:"8px", display:"flex", alignItems:"center", gap:"12px" },
  courseDot:   { width:"10px", height:"10px", borderRadius:"50%", flexShrink:0 },
  courseInfo:  { flex:1 },
  courseName:  { fontSize:"14px", fontWeight:600, color:"#F0EFE8" },
  courseCode:  { fontSize:"12px", color:"#5C5B63", marginTop:"2px" },
  taskBadge:   { fontSize:"11px", fontWeight:500, padding:"3px 9px", borderRadius:"20px", background:"rgba(124,110,248,.12)", color:"#9D92FA" },
  delBtn:      { background:"none", border:"none", color:"#5C5B63", fontSize:"18px", cursor:"pointer", padding:"0 4px" },
  planCard:    { background:"#18181F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:"16px", padding:"13px 14px", marginBottom:"8px", display:"flex", alignItems:"center", gap:"12px" },
  check:       { width:"22px", height:"22px", borderRadius:"50%", border:"1.5px solid rgba(255,255,255,0.2)", flexShrink:0, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  checkDone:   { background:"#2ECC8A", border:"1.5px solid #2ECC8A" },
  planBody:    { flex:1 },
  planSubj:    { fontSize:"13px", fontWeight:600, color:"#F0EFE8" },
  planTask:    { fontSize:"12px", color:"#9998A0", marginTop:"2px" },
  planTime:    { fontSize:"11px", color:"#5C5B63", marginTop:"2px", fontFamily:"monospace" },
  startBtn:    { fontSize:"12px", fontWeight:600, color:"#7C6EF8", background:"rgba(124,110,248,.12)", border:"none", borderRadius:"8px", padding:"6px 12px", cursor:"pointer", fontFamily:"inherit", flexShrink:0 },
  empty:       { textAlign:"center", padding:"32px 20px" },
  emptyIcon:   { fontSize:"36px", marginBottom:"10px" },
  emptyTitle:  { fontSize:"16px", fontWeight:600, color:"#F0EFE8", marginBottom:"6px" },
  emptyDesc:   { fontSize:"13px", color:"#9998A0" },
  overlay:     { position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:200 },
  modalBox:    { background:"#18181F", borderRadius:"24px 24px 0 0", padding:"20px 20px 40px", width:"100%", maxWidth:"480px", maxHeight:"90vh", overflowY:"auto" },
  handle:      { width:"38px", height:"4px", background:"#2A2A36", borderRadius:"2px", margin:"0 auto 18px" },
  modalTitle:  { fontSize:"18px", fontWeight:700, color:"#F0EFE8", marginBottom:"18px" },
  formGroup:   { marginBottom:"14px" },
  formRow:     { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" },
  label:       { fontSize:"12px", fontWeight:600, color:"#9998A0", textTransform:"uppercase", letterSpacing:".06em", marginBottom:"6px", display:"block" },
  input:       { width:"100%", padding:"12px 14px", background:"#0F0F13", border:"1px solid rgba(255,255,255,0.13)", borderRadius:"8px", fontFamily:"inherit", fontSize:"14px", color:"#F0EFE8", outline:"none" },
  submitBtn:   { width:"100%", padding:"14px", background:"#7C6EF8", border:"none", borderRadius:"12px", fontFamily:"inherit", fontSize:"15px", fontWeight:600, color:"#fff", cursor:"pointer", marginTop:"4px" },
};